import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { GraduationCap, Clock, PlayCircle, Lock, CheckCircle2, Loader2, ArrowLeft, Smartphone, CreditCard, Download, FileText, Award } from "lucide-react";
import { LmsAssignments, LmsQuizzes } from "../components/LmsPhase2";

const OPERATORS = ["Orange Money", "MTN MoMo", "Moov Money", "Wave"];

export default function LmsCourse() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [learn, setLearn] = useState(null);
  const [active, setActive] = useState(null); // lesson
  const [pay, setPay] = useState(null); // {enrollment_id, amount_label}
  const [op, setOp] = useState(OPERATORS[0]);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sect, setSect] = useState("lecons");
  const videoRef = useRef(null);

  const loadCourse = () => api.get(`/lms/courses/${id}`).then(({ data }) => { setCourse(data); if (data.unlocked) loadLearn(); }).catch((e) => toast.error(apiError(e)));
  const loadLearn = () => api.get(`/lms/learn/${id}`).then(({ data }) => { setLearn(data); setActive((a) => a || data.lessons[0]); }).catch(() => {});
  useEffect(() => { loadCourse(); }, [id]);

  const enroll = async () => {
    if (!user) { toast.error("Connectez-vous pour vous inscrire"); nav("/auth", { state: { from: `/cours/${id}` } }); return; }
    try {
      const { data } = await api.post(`/lms/courses/${id}/enroll`);
      if (data.status === "enrolled" || data.status === "already") { toast.success("Inscription confirmée"); loadCourse(); }
      else if (data.status === "payment_required") setPay(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const payMomo = async () => {
    if (!phone.trim()) { toast.error("Numéro requis"); return; }
    setBusy(true);
    try { await api.post(`/lms/enrollments/${pay.enrollment_id}/pay/mobile-money`, { operator: op, phone }); toast.success("Paiement confirmé"); setPay(null); loadCourse(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const payStripe = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/lms/enrollments/${pay.enrollment_id}/pay/stripe`, { origin_url: window.location.origin, currency: "EUR" }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const markComplete = async (lesson) => {
    try { const { data } = await api.post(`/lms/learn/${id}/progress`, { lesson_id: lesson.id, completed: true }); toast.success("Leçon marquée comme vue"); setLearn((l) => ({ ...l, progress: data.progress, lessons: l.lessons.map((x) => x.id === lesson.id ? { ...x, completed: true, percent: 100 } : x) })); loadCourse(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const dlAuth = async (url, filename) => {
    try { const res = await api.get(url, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u); }
    catch (e) { toast.error(apiError(e, "Document indisponible")); }
  };

  if (!course) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>;

  const embed = (les) => les.video_type === "youtube" ? les.embed_url : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10" data-testid="lms-course-page">
      <button onClick={() => nav("/cours")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={15} /> Toutes les formations</button>

      {course.unlocked && learn ? (
        <>
        <div className="mb-4 flex gap-2">
          {[["lecons", "Leçons"], ["ressources", "Ressources"], ["devoirs", "Devoirs"], ["quiz", "Compositions"]].map(([k, l]) => (
            <button key={k} onClick={() => setSect(k)} data-testid={`lms-sect-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-medium ${sect === k ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {sect === "ressources" ? (
          <div className="space-y-6" data-testid="lms-resources">
            <div>
              <h3 className="mb-2 font-display text-lg font-semibold text-navy">Documents téléchargeables</h3>
              {(learn.resources || []).length === 0 ? <p className="text-sm text-slate-400">Aucun document joint à ce cours.</p> : (
                <div className="space-y-2">
                  {learn.resources.map((r) => (
                    <a key={r.id} href={r.url} download={r.name} target="_blank" rel="noreferrer" data-testid={`resource-${r.id}`}
                      className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-[#0E7C7B]">
                      <span className="flex items-center gap-2 text-navy"><FileText size={15} className="text-[#0E7C7B]" /> {r.name}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E7C7B]"><Download size={13} /> Télécharger</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="mb-2 font-display text-lg font-semibold text-navy">Vidéos hors-ligne</h3>
              <div className="space-y-2">
                {learn.lessons.map((l, i) => (
                  <div key={l.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm">
                    <span className="flex items-center gap-2 text-navy"><PlayCircle size={15} className="text-slate-400" /> {i + 1}. {l.title}</span>
                    {l.video_type === "upload" && l.video_url ? (
                      <a href={l.video_url} download target="_blank" rel="noreferrer" data-testid={`video-dl-${l.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E7C7B]"><Download size={13} /> Télécharger (MP4)</a>
                    ) : (
                      <span className="text-[11px] text-slate-400">Lecture en ligne uniquement</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-400">Les vidéos hébergées sur YouTube ne sont pas téléchargeables (limitation YouTube).</p>
            </div>
          </div>
        ) : sect === "devoirs" ? <LmsAssignments cid={id} /> : sect === "quiz" ? <LmsQuizzes cid={id} /> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="lms-player">
          <div>
            <div className="overflow-hidden rounded-2xl bg-black">
              <div className="aspect-video w-full">
                {active && (embed(active) ? <iframe src={embed(active)} title={active.title} className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="lms-video-frame" /> :
                  <video ref={videoRef} src={active.video_url} controls className="h-full w-full" data-testid="lms-video-file" />)}
              </div>
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-navy">{active?.title}</h1>
            <p className="text-sm text-slate-500">{course.title} · {course.instructor}</p>
            {active && !active.completed && <Button onClick={() => markComplete(active)} data-testid="lms-mark-complete" className="mt-3 rounded-full bg-[#0E7C7B] text-white"><CheckCircle2 size={15} className="mr-1" /> Marquer comme vu</Button>}
            {active?.completed && <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-emerald-600"><CheckCircle2 size={16} /> Leçon terminée</span>}
            {active?.video_type === "upload" && active?.video_url && <a href={active.video_url} download target="_blank" rel="noreferrer" data-testid="lms-video-download" className="ml-3 mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#0E7C7B]"><Download size={15} /> Télécharger la vidéo</a>}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3"><div className="h-2 w-full rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#0E7C7B]" style={{ width: `${learn.progress.percent}%` }} data-testid="lms-progress-bar" /></div><p className="mt-1 text-xs text-slate-500">{learn.progress.percent}% terminé</p></div>
            {learn.certificate_available && <Button onClick={() => dlAuth(`/lms/courses/${id}/certificate.pdf`, `certificat-${course.title}.pdf`)} data-testid="lms-course-certificate" className="mb-3 w-full rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0b6463]"><Award size={14} className="mr-1" /> Certificat de participation</Button>}
            <div className="space-y-1">
              {learn.lessons.map((l, i) => (
                <button key={l.id} onClick={() => setActive(l)} data-testid={`lms-lesson-item-${i}`} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${active?.id === l.id ? "bg-[#0E7C7B]/10 text-[#0E7C7B]" : "hover:bg-slate-50"}`}>
                  {l.completed ? <CheckCircle2 size={15} className="text-emerald-500" /> : <PlayCircle size={15} className="text-slate-400" />}
                  <span className="flex-1 truncate">{i + 1}. {l.title}</span>
                  <span className="text-[11px] text-slate-400">{l.duration_min}m</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        )}
        </>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
          <div>
            <div className="h-56 w-full overflow-hidden rounded-2xl bg-slate-100">
              {course.thumbnail_url ? <img src={course.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><GraduationCap size={44} className="text-slate-300" /></div>}
            </div>
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-[#0E7C7B]">{course.category}</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-navy">{course.title}</h1>
            <p className="mt-1 text-sm text-slate-500">{course.instructor}{course.instructor_title ? ` — ${course.instructor_title}` : ""}</p>
            <p className="mt-3 whitespace-pre-line text-slate-600">{course.description}</p>
            <h2 className="mt-6 font-display text-lg font-semibold text-navy">Programme ({course.lessons_count} leçons · {course.total_min} min)</h2>
            <div className="mt-2 space-y-1">
              {course.lessons.map((l, i) => (
                <div key={l.id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  {l.free_preview ? <PlayCircle size={15} className="text-emerald-500" /> : <Lock size={14} className="text-slate-400" />}
                  <span className="flex-1">{i + 1}. {l.title}</span>
                  {l.free_preview && <span className="text-[10px] font-semibold text-emerald-600">Aperçu gratuit</span>}
                  <span className="text-[11px] text-slate-400">{l.duration_min}m</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5">
            <p className="font-display text-3xl font-bold text-navy">{course.price_label}</p>
            {!pay ? (
              <Button onClick={enroll} data-testid="lms-enroll-btn" className="mt-4 w-full rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">{course.price_xof === 0 ? "S'inscrire gratuitement" : "S'inscrire à ce cours"}</Button>
            ) : (
              <div className="mt-4 space-y-3" data-testid="lms-payment-panel">
                <p className="text-sm font-semibold text-navy">Paiement — {pay.amount_label}</p>
                <div>
                  <p className="mb-1 flex items-center gap-1 text-xs text-slate-500"><Smartphone size={13} /> Mobile Money</p>
                  <select value={op} onChange={(e) => setOp(e.target.value)} className="mb-2 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="lms-momo-op">{OPERATORS.map((o) => <option key={o}>{o}</option>)}</select>
                  <Input placeholder="Numéro (+225…)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="lms-momo-phone" />
                  <Button onClick={payMomo} disabled={busy} data-testid="lms-momo-pay" className="mt-2 w-full rounded-full bg-navy text-white">{busy ? <Loader2 size={14} className="animate-spin" /> : "Payer par Mobile Money"}</Button>
                </div>
                <div className="border-t border-slate-100 pt-3">
                  <Button onClick={payStripe} disabled={busy} variant="outline" data-testid="lms-stripe-pay" className="w-full rounded-full"><CreditCard size={14} className="mr-1" /> Payer par carte (Stripe)</Button>
                </div>
              </div>
            )}
            <p className="mt-3 text-center text-[11px] text-slate-400">Cabinet CARIS Santé</p>
          </div>
        </div>
      )}
    </div>
  );
}
